import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Loader2, Plus,
  LayoutDashboard, BarChart3, Workflow, Activity, Store, Trash2,
  ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Play, Rocket,
  Clock, ChevronRight, FileText, Cpu, Settings, Eye, Edit2, X
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BlueprintFlowChart } from '@/components/BlueprintFlowChart';
import OperatorChat from '@/components/OperatorChat';
import OpenClawTab from '@/components/OpenClawTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  draft: { cls: 'bg-white/10 text-muted-foreground', label: 'Draft' },
  testing: { cls: 'bg-amber-500/20 text-amber-400', label: 'Testing' },
  live: { cls: 'bg-emerald-500/20 text-emerald-400', label: 'Live' },
  archived: { cls: 'bg-red-500/20 text-red-400', label: 'Archived' },
};

const TOOL_OPTIONS = [
  { id: 'voice', label: 'Voice AI', icon: '🎙️' },
  { id: 'email', label: 'Email', icon: '✉️' },
  { id: 'crm', label: 'Outbound Leads', icon: '👥' },
  { id: 'calendar', label: 'Calendar', icon: '📅' },
  { id: 'sms', label: 'SMS', icon: '💬' },
  { id: 'database', label: 'Database', icon: '🗃️' },
];

const WorkflowBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  // Views: home, wizard, detail
  const [view, setView] = useState('home');
  const [blueprints, setBlueprintsList] = useState([]);
  const [selectedBp, setSelectedBp] = useState(null);
  const [loading, setLoading] = useState(true);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(0); // 0=outcome, 1=summary, 2=integrations, 3=test, 4=deploy
  const [generating, setGenerating] = useState(false);
  const [outcome, setOutcome] = useState('');
  const [desiredOutput, setDesiredOutput] = useState('');
  const [selectedTools, setSelectedTools] = useState([]);
  const [constraints, setConstraints] = useState('');
  const [editInstruction, setEditInstruction] = useState('');
  const [editing, setEditing] = useState(false);

  // Test state
  const [testInput, setTestInput] = useState('{\n  "caller_name": "John Smith",\n  "message": "I need help"\n}');
  const [testTrace, setTestTrace] = useState(null);
  const [testing, setTesting] = useState(false);

  // Deploy state
  const [deploying, setDeploying] = useState(false);
  const [agents, setAgents] = useState([]);

  useEffect(() => { fetchBlueprints(); fetchAgents(); }, []);

  const fetchBlueprints = async () => {
    try {
      const res = await axios.get(`${API}/blueprints`);
      setBlueprintsList(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/agents`);
      setAgents(res.data);
    } catch (err) { console.error(err); }
  };

  const handleGenerate = async () => {
    if (!outcome.trim()) return toast.error('Describe what you want the workflow to do');
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/blueprints`, {
        outcome: outcome.trim(),
        desired_output: desiredOutput.trim(),
        tools: selectedTools,
        constraints: constraints.split('\n').filter(Boolean),
      });
      setSelectedBp(res.data);
      setWizardStep(1);
      toast.success('Blueprint generated!');
      fetchBlueprints();
    } catch (err) { toast.error('Generation failed'); }
    finally { setGenerating(false); }
  };

  const handleEditInEnglish = async () => {
    if (!editInstruction.trim() || !selectedBp) return;
    setEditing(true);
    try {
      const res = await axios.post(`${API}/blueprints/${selectedBp.id}/edit`, { edit_instruction: editInstruction.trim() });
      setSelectedBp(res.data);
      setEditInstruction('');
      toast.success('Blueprint updated!');
      fetchBlueprints();
    } catch (err) { toast.error('Edit failed'); }
    finally { setEditing(false); }
  };

  const handleTest = async () => {
    if (!selectedBp) return;
    setTesting(true);
    try {
      let parsed = {};
      try { parsed = JSON.parse(testInput); } catch { parsed = { input: testInput }; }
      const res = await axios.post(`${API}/blueprints/${selectedBp.id}/test`, { test_input: parsed });
      setTestTrace(res.data);
      // Refresh blueprint (status may have changed to testing)
      const bpRes = await axios.get(`${API}/blueprints/${selectedBp.id}`);
      setSelectedBp(bpRes.data);
      toast.success('Simulation complete!');
      fetchBlueprints();
    } catch (err) { toast.error('Test failed'); }
    finally { setTesting(false); }
  };

  const handleDeploy = async () => {
    if (!selectedBp) return;
    setDeploying(true);
    try {
      const res = await axios.post(`${API}/blueprints/${selectedBp.id}/deploy`, {});
      toast.success('Agent deployed!');
      const bpRes = await axios.get(`${API}/blueprints/${selectedBp.id}`);
      setSelectedBp(bpRes.data);
      fetchBlueprints();
      fetchAgents();
      setWizardStep(4);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'object') {
        toast.error(`Cannot deploy: ${detail.blockers?.join(', ')}`);
      } else {
        toast.error(detail || 'Deploy failed');
      }
    } finally { setDeploying(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blueprint?')) return;
    try {
      await axios.delete(`${API}/blueprints/${id}`);
      if (selectedBp?.id === id) { setSelectedBp(null); setView('home'); }
      fetchBlueprints();
      toast.success('Deleted');
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openBlueprint = (bp) => {
    setSelectedBp(bp);
    setView('detail');
    setWizardStep(1);
    setTestTrace(null);
  };

  const startNewWizard = () => {
    setView('wizard');
    setWizardStep(0);
    setSelectedBp(null);
    setOutcome('');
    setDesiredOutput('');
    setSelectedTools([]);
    setConstraints('');
    setTestTrace(null);
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Demand Intel', path: '/demand' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: Activity, label: 'API Hub', path: '/hub' },
  ];

  // ==================== RENDER ====================

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <HSLogo size={32} />
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>);
            })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p><p className="text-xs text-muted-foreground truncate">{user?.email}</p></div>
            </div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              {view !== 'home' && <button onClick={() => { setView('home'); setSelectedBp(null); }} className="p-1.5 text-muted-foreground hover:text-white"><ArrowLeft className="w-5 h-5" /></button>}
              <h1 className="text-lg font-bold" data-testid="builder-title">Workflow Builder</h1>
            </div>
            <div className="flex items-center gap-2">
              {selectedBp && (
                <Button onClick={() => setChatOpen(!chatOpen)} variant={chatOpen ? 'default' : 'outline'} className={`rounded-sm h-9 text-sm ${chatOpen ? 'bg-emerald-500 text-black' : 'border-white/10'}`} data-testid="chat-toggle">
                  <Cpu className="w-4 h-4 mr-1" /> Chat
                </Button>
              )}
              <Button onClick={startNewWizard} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid="new-blueprint-btn">
                <Plus className="w-4 h-4 mr-1" /> New Workflow
              </Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {/* HOME VIEW */}
          {view === 'home' && (
            <div className="space-y-6 max-w-5xl mx-auto">
              {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : (
                <>
                  {/* Agents */}
                  {agents.length > 0 && (
                    <div>
                      <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2"><Rocket className="w-4 h-4" /> Live Agents</h2>
                      <div className="grid md:grid-cols-2 gap-3">
                        {agents.map(a => (
                          <div key={a.id} className="bg-[#0a0a0a] border border-emerald-500/20 p-4 flex items-center justify-between" data-testid={`agent-${a.id}`}>
                            <div>
                              <p className="font-medium text-sm">{a.name}</p>
                              <p className="text-xs text-muted-foreground">v{a.blueprint_version} · {a.metrics?.total_runs || 0} runs</p>
                            </div>
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 ${a.status === 'running' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{a.status.toUpperCase()}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Blueprints list */}
                  <div>
                    <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Blueprints</h2>
                    {blueprints.length === 0 ? (
                      <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                        <Workflow className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
                        <h3 className="text-lg font-bold mb-2">No workflows yet</h3>
                        <p className="text-muted-foreground text-sm mb-6 max-w-md mx-auto">Describe what you want in plain English. AI generates the workflow blueprint, you test it, then deploy.</p>
                        <Button onClick={startNewWizard} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm"><Plus className="w-4 h-4 mr-2" /> Create Your First Workflow</Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {blueprints.map(bp => {
                          const st = STATUS_STYLES[bp.status] || STATUS_STYLES.draft;
                          return (
                            <div key={bp.id} onClick={() => openBlueprint(bp)} className="bg-[#0a0a0a] border border-white/5 p-4 flex items-center justify-between cursor-pointer hover:border-white/10 transition-colors group" data-testid={`bp-${bp.id}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-3">
                                  <h3 className="font-bold text-sm truncate group-hover:text-emerald-400 transition-colors">{bp.name}</h3>
                                  <span className={`text-[10px] font-mono px-1.5 py-0.5 ${st.cls}`}>{st.label}</span>
                                  <span className="text-[10px] text-muted-foreground font-mono">v{bp.version}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 truncate">{bp.description || bp.outcome_prompt}</p>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <span className="text-xs text-muted-foreground">{bp.states?.length || 0} states</span>
                                <button onClick={(e) => { e.stopPropagation(); handleDelete(bp.id); }} className="p-1.5 text-muted-foreground hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* WIZARD / DETAIL VIEW */}
          {(view === 'wizard' || view === 'detail') && (
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Step indicator */}
              <div className="flex items-center gap-0" data-testid="step-indicator">
                {['Outcome', 'Summary', 'Integrations', 'Test', 'Deploy', 'Open Claw'].map((step, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div className={`flex-1 h-0.5 ${wizardStep >= i ? 'bg-emerald-500' : 'bg-white/10'}`} />}
                    <button onClick={() => { if (selectedBp || i === 0) setWizardStep(i); }} className={`flex flex-col items-center ${wizardStep === i ? '' : 'opacity-50'}`}>
                      <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-mono ${wizardStep >= i ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-muted-foreground'}`}>{i + 1}</div>
                      <span className="text-[10px] mt-1 hidden sm:block">{step}</span>
                    </button>
                  </React.Fragment>
                ))}
              </div>

              {/* Step 0: Outcome Prompt */}
              {wizardStep === 0 && (
                <div className="space-y-6" data-testid="step-outcome">
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold mb-2">What should this workflow do?</h2>
                    <p className="text-muted-foreground">Describe the outcome in plain English. AI builds the blueprint.</p>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Outcome *</label>
                    <Textarea value={outcome} onChange={e => setOutcome(e.target.value)} placeholder="Answer incoming calls for a roofing company, qualify leads, and book appointments..." className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[100px] text-base" data-testid="outcome-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Desired Output</label>
                    <Input value={desiredOutput} onChange={e => setDesiredOutput(e.target.value)} placeholder="Qualified lead record with name, phone, project type, and booked appointment" className="bg-[#0a0a0a] border-white/10 rounded-sm" data-testid="output-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Tools</label>
                    <div className="flex flex-wrap gap-2">
                      {TOOL_OPTIONS.map(tool => (
                        <button key={tool.id} onClick={() => setSelectedTools(prev => prev.includes(tool.id) ? prev.filter(t => t !== tool.id) : [...prev, tool.id])}
                          className={`px-3 py-2 border text-sm transition-all ${selectedTools.includes(tool.id) ? 'border-emerald-500/50 bg-emerald-500/10 text-emerald-400' : 'border-white/10 text-muted-foreground hover:border-white/20'}`}
                          data-testid={`tool-${tool.id}`}>
                          {tool.icon} {tool.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Constraints (one per line)</label>
                    <Textarea value={constraints} onChange={e => setConstraints(e.target.value)} placeholder="Business hours only&#10;Escalate emergencies&#10;Professional tone" className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[80px]" data-testid="constraints-input" />
                  </div>
                  <Button onClick={handleGenerate} disabled={generating || !outcome.trim()} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-12 text-base" data-testid="generate-btn">
                    {generating ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Generating Blueprint...</> : <><Cpu className="w-5 h-5 mr-2" /> Generate Workflow</>}
                  </Button>
                </div>
              )}

              {/* Step 1: Summary */}
              {wizardStep === 1 && selectedBp && (
                <div className="space-y-6" data-testid="step-summary">
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-bold">{selectedBp.name}</h2>
                      <p className="text-sm text-muted-foreground mt-1">{selectedBp.description}</p>
                    </div>
                    <span className="text-xs font-mono text-muted-foreground">v{selectedBp.version}</span>
                  </div>

                  {/* Summary text */}
                  {selectedBp.summary_text && (
                    <div className="bg-[#0a0a0a] border border-white/5 p-4">
                      <p className="text-sm">{selectedBp.summary_text}</p>
                    </div>
                  )}

                  {/* Structured view */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-[#0a0a0a] border border-white/5 p-4">
                      <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Triggers</h3>
                      <div className="space-y-1">
                        {(selectedBp.triggers || []).map((t, i) => (
                          <div key={i} className="text-sm flex items-center gap-2"><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />{t.label || t.type}</div>
                        ))}
                      </div>
                    </div>
                    <div className="bg-[#0a0a0a] border border-white/5 p-4">
                      <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Outputs</h3>
                      <div className="space-y-1">
                        {Object.entries(selectedBp.output_schema || {}).map(([k, v]) => (
                          <div key={k} className="text-sm font-mono"><span className="text-emerald-400">{k}</span>: <span className="text-muted-foreground">{v}</span></div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Visual Workflow Map */}
                  <BlueprintFlowChart
                    triggers={selectedBp.triggers}
                    states={selectedBp.states}
                    outputSchema={selectedBp.output_schema}
                    onNodeClick={(node) => toast.info(`${node.label} (${node.type})`)}
                  />

                  {/* States / Actions flow */}
                  <div className="bg-[#0a0a0a] border border-white/5 p-4">
                    <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Workflow States</h3>
                    <div className="space-y-2">
                      {(selectedBp.states || []).map((state, i) => (
                        <div key={i} className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0">
                          <div className={`w-7 h-7 flex items-center justify-center flex-shrink-0 text-xs font-mono ${
                            state.type === 'decision' ? 'bg-amber-500/10 text-amber-400' :
                            state.type === 'wait' ? 'bg-blue-500/10 text-blue-400' :
                            'bg-emerald-500/10 text-emerald-400'
                          }`}>{i + 1}</div>
                          <div className="flex-1">
                            <p className="text-sm font-medium">{state.name}</p>
                            <p className="text-xs text-muted-foreground">{state.description}</p>
                            {state.next?.length > 0 && <p className="text-[10px] text-muted-foreground mt-1 font-mono">→ {state.next.join(', ')}</p>}
                          </div>
                          <span className="text-[10px] px-1.5 py-0.5 bg-white/5 text-muted-foreground uppercase">{state.type}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Escalation */}
                  {(selectedBp.escalation_rules || []).length > 0 && (
                    <div className="bg-[#0a0a0a] border border-amber-500/20 p-4">
                      <h3 className="text-xs text-amber-400 uppercase tracking-wider mb-3">Escalation Rules</h3>
                      {selectedBp.escalation_rules.map((r, i) => (
                        <div key={i} className="text-sm mb-1"><span className="text-amber-400">If</span> {r.condition} <span className="text-amber-400">→</span> {r.action}</div>
                      ))}
                    </div>
                  )}

                  {/* Validation */}
                  {selectedBp.validation && (
                    <div className={`border p-4 ${selectedBp.validation.valid ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {selectedBp.validation.valid ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <AlertTriangle className="w-4 h-4 text-red-400" />}
                        <span className="text-sm font-medium">{selectedBp.validation.valid ? 'Ready for testing' : 'Issues found'}</span>
                      </div>
                      {(selectedBp.validation.warnings || []).map((w, i) => (
                        <p key={i} className="text-xs text-amber-400 ml-6">⚠ {w}</p>
                      ))}
                      {(selectedBp.validation.blockers || []).map((b, i) => (
                        <p key={i} className="text-xs text-red-400 ml-6">✕ {b}</p>
                      ))}
                    </div>
                  )}

                  {/* Edit in English */}
                  <div className="bg-[#0a0a0a] border border-white/5 p-4">
                    <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Edit in English</h3>
                    <div className="flex gap-2">
                      <Input value={editInstruction} onChange={e => setEditInstruction(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleEditInEnglish()} placeholder="e.g. Add a step to verify the caller email" className="flex-1 bg-black/30 border-white/10 rounded-sm text-sm" data-testid="edit-english-input" />
                      <Button onClick={handleEditInEnglish} disabled={editing || !editInstruction.trim()} className="bg-violet-600 text-white hover:bg-violet-500 rounded-sm px-4" data-testid="edit-english-btn">
                        {editing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Edit2 className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button onClick={() => setWizardStep(0)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                    <Button onClick={() => setWizardStep(2)} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm">Integrations <ArrowRight className="w-4 h-4 ml-1" /></Button>
                  </div>
                </div>
              )}

              {/* Step 2: Integrations */}
              {wizardStep === 2 && selectedBp && (
                <div className="space-y-6" data-testid="step-integrations">
                  <h2 className="text-xl font-bold">Required Integrations</h2>
                  <p className="text-muted-foreground text-sm">These integrations are needed for your workflow to run. Configure them now or skip and do it later.</p>
                  <div className="space-y-3">
                    {(selectedBp.integrations_required || []).length > 0 ? (selectedBp.integrations_required || []).map((int_name, i) => (
                      <div key={i} className="bg-[#0a0a0a] border border-white/5 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-violet-500/10 flex items-center justify-center text-violet-400"><Settings className="w-5 h-5" /></div>
                          <div>
                            <p className="font-medium text-sm capitalize">{int_name}</p>
                            <p className="text-xs text-muted-foreground">Configure via API Hub</p>
                          </div>
                        </div>
                        <Button onClick={() => navigate('/hub')} variant="outline" size="sm" className="border-white/10 rounded-sm text-xs">Configure</Button>
                      </div>
                    )) : (
                      <div className="bg-[#0a0a0a] border border-emerald-500/20 p-6 text-center">
                        <CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                        <p className="text-sm">No external integrations required</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => setWizardStep(1)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                    <Button onClick={() => setWizardStep(3)} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm">Test Workflow <ArrowRight className="w-4 h-4 ml-1" /></Button>
                  </div>
                </div>
              )}

              {/* Step 3: Test */}
              {wizardStep === 3 && selectedBp && (
                <div className="space-y-6" data-testid="step-test">
                  <h2 className="text-xl font-bold">Test Your Workflow</h2>
                  <p className="text-muted-foreground text-sm">Simulate a run with test input. See the execution trace.</p>
                  <div>
                    <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">Test Input (JSON)</label>
                    <Textarea value={testInput} onChange={e => setTestInput(e.target.value)} className="bg-[#0a0a0a] border-white/10 rounded-sm min-h-[100px] font-mono text-sm" data-testid="test-input" />
                  </div>
                  <Button onClick={handleTest} disabled={testing} className="w-full bg-violet-600 text-white hover:bg-violet-500 font-bold rounded-sm h-10" data-testid="run-test-btn">
                    {testing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Simulating...</> : <><Play className="w-4 h-4 mr-2" /> Run Simulation</>}
                  </Button>

                  {/* Execution trace */}
                  {testTrace && (
                    <div className="bg-[#0a0a0a] border border-white/5 p-4" data-testid="execution-trace">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Execution Trace</h3>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-mono px-1.5 py-0.5 ${testTrace.success ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{testTrace.success ? 'SUCCESS' : 'FAILED'}</span>
                          <span className="text-xs text-muted-foreground font-mono">{testTrace.total_duration_ms}ms</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {(testTrace.steps || []).map((step, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="flex flex-col items-center">
                              <div className={`w-6 h-6 flex items-center justify-center rounded-full text-[10px] font-mono ${step.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : step.status === 'failed' ? 'bg-red-500/20 text-red-400' : 'bg-white/10 text-muted-foreground'}`}>{step.step}</div>
                              {i < (testTrace.steps || []).length - 1 && <div className="w-px h-6 bg-white/10 mt-1" />}
                            </div>
                            <div className="flex-1 pb-2">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium">{step.action}</p>
                                <span className="text-[10px] text-muted-foreground font-mono">{step.duration_ms}ms</span>
                              </div>
                              {step.output && Object.keys(step.output).length > 0 && (
                                <pre className="text-[10px] text-muted-foreground mt-1 font-mono overflow-hidden">{JSON.stringify(step.output, null, 0).slice(0, 120)}</pre>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      {testTrace.warnings?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/5">
                          {testTrace.warnings.map((w, i) => <p key={i} className="text-xs text-amber-400">⚠ {w}</p>)}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button onClick={() => setWizardStep(2)} variant="outline" className="border-white/10 rounded-sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
                    <Button onClick={handleDeploy} disabled={deploying || !selectedBp.validation?.can_deploy} className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="deploy-btn">
                      {deploying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Deploying...</> : <><Rocket className="w-4 h-4 mr-2" /> Deploy Live</>}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4: Deployed */}
              {wizardStep === 4 && selectedBp && (
                <div className="space-y-6 text-center" data-testid="step-deploy">
                  <div className="py-8">
                    <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Rocket className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h2 className="text-2xl font-bold mb-2">Workflow Deployed!</h2>
                    <p className="text-muted-foreground">Your agent is now live and ready to process requests.</p>
                  </div>
                  <div className="bg-[#0a0a0a] border border-emerald-500/20 p-6 text-left max-w-md mx-auto">
                    <h3 className="font-bold mb-2">{selectedBp.name}</h3>
                    <p className="text-sm text-muted-foreground mb-3">{selectedBp.description}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono text-muted-foreground">Version {selectedBp.version}</span>
                      <span className="bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 font-mono">LIVE</span>
                    </div>
                  </div>
                  <div className="flex justify-center gap-3">
                    <Button onClick={() => { setView('home'); fetchBlueprints(); fetchAgents(); }} variant="outline" className="border-white/10 rounded-sm">View All Workflows</Button>
                    <Button onClick={startNewWizard} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm"><Plus className="w-4 h-4 mr-1" /> Create Another</Button>
                  </div>
                </div>
              )}

              {/* Step 5: Open Claw */}
              {wizardStep === 5 && selectedBp && (
                <div data-testid="step-openclaw">
                  <OpenClawTab blueprintId={selectedBp.id} />
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Operator Chat Panel */}
      {chatOpen && selectedBp && (
        <div className="w-96 flex-shrink-0 h-screen sticky top-0" data-testid="chat-panel">
          <OperatorChat
            blueprintId={selectedBp.id}
            blueprintVersion={selectedBp.version || 1}
            onPatchApplied={async (newVersion) => {
              const res = await axios.get(`${API}/blueprints/${selectedBp.id}`);
              setSelectedBp(res.data);
              fetchBlueprints();
            }}
            onSimulate={() => setWizardStep(3)}
            onDeploy={() => handleDeploy()}
          />
        </div>
      )}
    </div>
  );
};

export default WorkflowBuilder;
